/* eslint-disable simple-import-sort/imports */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '../../../../../state/project-state/project-state';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { DropdownSuggestion } from '../../../../dropdown-suggestion/dropdown-suggesion';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Stack } from '../../../../stack/stack';
import styled from 'styled-components';

type Props = {
  onClose: (user?: { id: string; email: string }) => void;
};

type Suggestion = { id: string; name: string };

const CreatedByDialog = (props: Props) => {
  const { project } = useContext(ProjectContext);
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<Suggestion | undefined>(undefined);

  const close = (user?: { id: string; email: string }) => props.onClose(user);

  const users = React.useMemo(
    () =>
      [...(project?.users ?? []), ...(project?.companyAdmins ?? [])] as {
        id: string;
        email: string;
        firstName?: string;
        lastName?: string;
      }[],
    [project?.users, project?.companyAdmins],
  );

  // Create lookup map for O(1) access
  const userMap = React.useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const suggestions: Suggestion[] = users.map((u) => ({ id: u.id, name: u.email }));

  const filterPredicate = React.useCallback(
    (input: string, s: Suggestion) => {
      const u = userMap.get(s.id);
      const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(' ').toLowerCase();
      return s.name.toLowerCase().includes(input) || (fullName ? fullName.includes(input) : false);
    },
    [userMap],
  );

  const renderSuggestion = React.useCallback(
    (s: Suggestion) => {
      const u = userMap.get(s.id);
      const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(' ');
      return (
        <SuggestionLayout>
          <NameLine>{fullName || u?.email}</NameLine>
          {fullName && <EmailLine>{u?.email}</EmailLine>}
        </SuggestionLayout>
      );
    },
    [userMap],
  );

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} onClose={() => close()}>
      {{
        header: t('topArea.filter.createdBy', { ns: 'skyviewAssetMenu' }),
        content: (
          <Stack spacing={1}>
            <LabelledContainer text={t('topArea.filter.selectUser', { ns: 'skyviewAssetMenu' })}>
              <DropdownSuggestion
                clearOnSelection={true}
                filterPredicate={filterPredicate}
                id="createdByUser"
                placeholder={t('topArea.filter.noneSelectedPlaceholder', {
                  ns: 'skyviewAssetMenu',
                })}
                portalToBody={true}
                renderSuggestion={renderSuggestion}
                suggestions={suggestions}
                value={selected?.id}
                onSuggestionSelected={(s) => setSelected(s)}
              />
            </LabelledContainer>
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  if (selected) {
                    const user = users.find((u) => u.id === selected.id);
                    close(user ? { id: user.id, email: user.email } : undefined);
                  } else {
                    close();
                  }
                }}
              >
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={() => close()}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { CreatedByDialog };

const SuggestionLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const NameLine = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.color.gray.darkest};
  font-family: ${({ theme }) => theme.font['font-family']};
`;

const EmailLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray.medium};
`;
